import React, { useEffect, useState } from 'react';
import { Bubble } from 'react-chartjs-2';
import { useDispatch, useSelector } from 'react-redux';
import { BubbleDataCard, ModalPosition } from '../../../models/DisbursementTimeIndicatorInterfaces';
import { getProjectsSimpleTableIndicator } from '../../../redux/actions/indicatorProjects';
import { DisbursementTimeCardModal } from '../../Util/Modals/DisbursementTimeCardModal/DisbursementTimeCardModal';
import { handleSelectedDefaultWithProjects, setDefaultValueToCompositionFilter } from '../PortfolioDetailHeader/Util';
import { handleModalPositionLimits, parseToChartBubbles, setOptions } from './Util';
import styles from './DisbursementTime.module.scss';
import { useTranslation } from 'react-i18next';
import { isMobile } from '../../../services/booleanUtil';
import IndicatorDetailFilter from '../../../models/IndicatorDetailFilter';
import { YEARS_OF_TOLERANCE } from '../../Util/Modals/CustomizableReports/LargeImagesComponents/Util';
import FilterIndicators from '../../../models/FilterIndicators';
import { AppMenuTabs } from '../../MenuTabs/userTabsInterface';
import { setIndicatorResponse, setLoading } from '../../../redux/actions/dataTable';
import { getNewFilteredIndicators } from '../../../api/indicatorApi';
import { translateMultipleText } from '../../../services/stringUtil';
import { emptyProjects } from '../../Portfolio/PortfolioUtil';
import { processIndicators } from '../../Portfolio/indicatorHelper';
import { ProjectProductTypes } from '../../../models/ProjectProductTypes';
import { arrayMove } from 'react-sortable-hoc';
import { addIndicators } from '../../../redux/actions/indicators';
import { setCurrentIndicator } from '../../../redux/actions/currentIndicator';
import { resetIndicatorDetailFilter } from '../../../redux/actions/indicatorDetailFilters';
import { updateCreatingOrEditing } from '../../../redux/actions/menuTabs';
import Filter from '../../Portfolio/Filter/Filter';
import ErrorMessageView from '../../Util/ErrorMessage/ErrorMessageView';
import FiltersErrorMessage from '../../Util/Errors/FiltersErrorMessage/FiltersErrorMessage';
const EMPTY_MODAL_POSITION: ModalPosition = {
  x: 0,
  y: 0,
};

const EMPTY_BUBBLE_DATA: BubbleDataCard = {
  code: '',
  title: '',
  loanNumber: '',
  yearsOfLegalValidity: '0',
  currentDisbursement: '0',
  pendingDisbursement: '0',
  extension: false,
  projectId: 0,
};

export const DisbursementTime = () => {
  const { t } = useTranslation();
  const { container, bottomLabel, chartContainer, leftLabel, noMatchingFilters } = styles;
  const { currentIndicator } = useSelector<any, any>(state => state);
  const { tabIndicatorValues } = currentIndicator;
  const [openModal, setOpenModal] = useState(false);
  const [bubbleData, setBubbleData] = useState<BubbleDataCard>(EMPTY_BUBBLE_DATA);
  const [modalPosition, setModalPosition] = useState<ModalPosition>(EMPTY_MODAL_POSITION);
  const [bubbles, setBubbles] = useState<any>([]);
  const [hasError, setHasError] = useState(false);
  const [hasAttemptedLoad, setHasAttemptedLoad] = useState(false);
  const [showFiltersError, setShowFiltersError] = useState(false);
  const { projectTypeFilters } = useSelector<any, any>(state => state.filterIndicators.common);
  const { projectTypeFilterOnDetail } = useSelector<any, IndicatorDetailFilter>(state => state.indicatorDetailFilter);
  const { indicatorProjects } = useSelector<any, any>(state => state);
  const dispatch = useDispatch();
  const [filterApplied, setFilterApplied] = useState(
    setDefaultValueToCompositionFilter(handleSelectedDefaultWithProjects(projectTypeFilters, tabIndicatorValues)),
  );
  const bubblesAxis = localStorage.getItem('bubblesAxis');
  const { upperY, bottomY, leftX, rightX } = bubblesAxis
    ? JSON.parse(bubblesAxis)
    : {
      upperY: 100,
      bottomY: -10,
      leftX: -1,
      rightX: Math.round(Math.max(...indicatorProjects.map(i => parseFloat(i.field1)))) + YEARS_OF_TOLERANCE,
    };
  const axis = { upperY, bottomY, leftX, rightX };

  // Filter
  const filterIndicators = useSelector<any, FilterIndicators>(state => state.filterIndicators);
  const stateProjectProductTypes = useSelector<any, FilterIndicators>(
    state => state.indicatorPayload.payload.projectProductTypes,
  );
  const menuTabs = useSelector<any, AppMenuTabs>(state => state.menuTabs);
  const portfolioSelectedTab = menuTabs.portfolioSelectedTab;
  const creatingOrEditing = menuTabs.creatingOrEditing;
  const [isLoading, setIsLoading] = React.useState(false);
  const [totalProjects, setTotalProjects] = React.useState(false);

  const projectTypeFilterChange = {
    ...filterIndicators,
    common: { ...filterIndicators.common, projectTypeFilters: stateProjectProductTypes },
    projectTypeFilters: stateProjectProductTypes,
  };

  const handleCommonFilterChange = async () => {

    dispatch(setLoading(true));
    setIsLoading(true);

    try {

      const indicatorsToProcess = await getNewFilteredIndicators(
        {},
        { projectProductTypes: stateProjectProductTypes },
        {
          ...projectTypeFilterChange.common,
        }
      );

      // normalizar para mantener compatibilidad
      const normalizedProjects = indicatorsToProcess?.indicatorsData || [];

      // Mantener la logica nueva
      dispatch(setIndicatorResponse(indicatorsToProcess));
      startProcessIndicators(
        indicatorsToProcess,
        translateMultipleText(` ${" "} ${portfolioSelectedTab.title}`, t),
        filterIndicators.common.projectTypeFilters
      );
      setTotalProjects(emptyProjects(indicatorsToProcess.totalProjects));

      // Compatibilidad con la version anterior y llenar indicatorProjects con un array

      dispatch(getProjectsSimpleTableIndicator(currentIndicator.id));

    } catch (err) {
      console.error("[handleCommonFilterChange] ERROR fetching data:", err);
    } finally {
      dispatch(setLoading(false));
      setIsLoading(false);
      setHasAttemptedLoad(true);
      console.log("[handleCommonFilterChange] finished");
    }
  };

  const startProcessIndicators = (inds, titleFromTab, projectTypeFilter = [ProjectProductTypes.Loan]) => {
    const result = processIndicators(inds, {
      titleFromTab,
      projectTypeFilter,
    });
    const elementSummary: any = result.find(elem => elem.id === 15);
    const indexSummary: number = result.indexOf(elementSummary);
    const sendEndSummary = arrayMove(result, indexSummary, result.length);

    dispatch(addIndicators(sendEndSummary));
    dispatch(setCurrentIndicator(sendEndSummary?.find(data => data.indicatorKey === 'DISBURSEMENT_OVER_TIME')));
    setIsLoading(false);
  };


  useEffect(() => {
    try {
      const parsedData = parseToChartBubbles(indicatorProjects, projectTypeFilterOnDetail, isMobile() ? 10 : 100);
      setBubbles(parsedData);
      setHasError(false);
    } catch (err) {
      setHasError(true);
      console.error('[DisbursementTime] ERROR parsing chart data:', err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [indicatorProjects]);

  useEffect(() => {
    if (projectTypeFilterOnDetail !== null) {
      setFilterApplied(projectTypeFilterOnDetail);
      getFilteredSimpleIndicators(dispatch, currentIndicator.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectTypeFilterOnDetail, filterIndicators.common]);

  useEffect(() => {
    if (
      filterIndicators.common.coReponsibleDivisions.length > 0 ||
      filterIndicators.common.countryId.length > 0 ||
      filterIndicators.common.divisionId.length > 0 ||
      filterIndicators.common.interestIssues.length > 0 ||
      filterIndicators.common.participantDivisions.length > 0 ||
      filterIndicators.common.regionId.length > 0 ||
      filterIndicators.common.sectors.length > 0 ||
      filterIndicators.common.teamLeaders.length > 0
    )
      handleCommonFilterChange();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterIndicators.common]);

  useEffect(() => {
    dispatch(resetIndicatorDetailFilter());
    if (
      !menuTabs.firstLoad &&
      // tabId0OrCreatingEditing(menuTabs, creatingOrEditing) &&
      filterIndicators.common.coReponsibleDivisions.length === 0 &&
      filterIndicators.common.countryId.length === 0 &&
      filterIndicators.common.divisionId.length === 0 &&
      filterIndicators.common.interestIssues.length === 0 &&
      filterIndicators.common.participantDivisions.length === 0 &&
      filterIndicators.common.regionId.length === 0 &&
      filterIndicators.common.sectors.length === 0 &&
      filterIndicators.common.teamLeaders.length === 0
    ) {
      handleCommonFilterChange();
      if (creatingOrEditing) {
        dispatch(updateCreatingOrEditing(false));
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterIndicators.common]);

  const getElementAtEvent = element => {
    if (!element.length) {
      return;
    }

    try {
      setHasError(false);
      !isMobile() && setModalPosition(handleModalPositionLimits(element));
      const modalData = bubbles.datasets[element[0]._datasetIndex].data[element[0]._index].modalData;

      setBubbleData(modalData);
      setOpenModal(true);
    } catch (err) {
      setHasError(true);
      console.error('[DisbursementTime.getElementAtEvent] ERROR setting modal data:', err);
    }
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const hasMissingFilters =
    !filterIndicators?.common ||
    !Array.isArray(filterIndicators.common.projectTypeFilters) ||
    filterIndicators.common.projectTypeFilters.length === 0;

  const hasInvalidBubbles =
    !bubbles ||
    !Array.isArray(bubbles.datasets) ||
    bubbles.datasets.length === 0 ||
    !bubbles.datasets.some(ds => Array.isArray(ds.data) && ds.data.length > 0);

  const hasAllZeroBubbles =
    Array.isArray(bubbles?.datasets) &&
    bubbles.datasets.length > 0 &&
    bubbles.datasets.every(
      ds =>
        Array.isArray(ds.data) &&
        ds.data.length > 0 &&
        ds.data.every(point => Number(point?.x) === 0 && Number(point?.y) === 0 && Number(point?.r) === 0),
    );

  const shouldShowError = hasError || (hasAttemptedLoad && !isLoading && hasMissingFilters);
  const shouldShowFiltersError = showFiltersError;

  useEffect(() => {
    if (hasAttemptedLoad && !isLoading && (hasInvalidBubbles || hasAllZeroBubbles)) {
      const timer = setTimeout(() => setShowFiltersError(true), 1000);
      return () => clearTimeout(timer);
    }
    setShowFiltersError(false);
  }, [hasAttemptedLoad, isLoading, hasInvalidBubbles, hasAllZeroBubbles]);

  useEffect(() => {
    console.log("bubbles: ", bubbles);
  }, [bubbles]);


  return (
    <>
      {!shouldShowError ? (
        shouldShowFiltersError ? (
          <div className={noMatchingFilters}>
            <FiltersErrorMessage isTable={true} />
          </div>
        ) : (
          <>
            <div className={container}>
              <div className={leftLabel}>{`${t('percentage_sign')} ${t('disbursed')}`}</div>
              <div className={chartContainer}>
                <Bubble
                  data={bubbles}
                  options={setOptions(filterApplied, axis)}
                  getElementAtEvent={element => getElementAtEvent(element)}
                />
                <DisbursementTimeCardModal
                  isOpen={openModal}
                  closeModal={handleCloseModal}
                  data={bubbleData}
                  modalPosition={modalPosition}
                />
              </div>
            </div>
            <div className={bottomLabel}>{t('years_of_legal_validity')}</div>
          </>
        )
      ) : (
        <div className={noMatchingFilters}>
          <ErrorMessageView
            title={t('title_error_message_view')}
            description={t('desc_error_message_tab')}
            reload={() => handleCommonFilterChange()}
            isIndicator={true}
          />
        </div>
      )}
    </>
  );
};

const getFilteredSimpleIndicators = (d, currentIndicatorId, projectTypeFilterChange?) => {
  return d(getProjectsSimpleTableIndicator(currentIndicatorId, undefined, projectTypeFilterChange));
};
